import React from 'react';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../contexts/LanguageContext';

const RiskGauge = ({ score = 0, size = 'md', showLabel = true }) => {
    const { t, language } = useLanguage();
    
    // Calculate color based on score
    const getColor = (score) => {
        if (score <= 30) return { stroke: '#10B981', bg: 'bg-emerald-500', label: t('lowRisk') };
        if (score <= 70) return { stroke: '#F59E0B', bg: 'bg-amber-500', label: t('mediumRisk') };
        return { stroke: '#E11D48', bg: 'bg-rose-500', label: t('highRisk') };
    };

    const { stroke, bg, label } = getColor(score);
    
    // Size configurations
    const sizes = {
        sm: { width: 80, strokeWidth: 8, fontSize: 'text-xl' },
        md: { width: 120, strokeWidth: 10, fontSize: 'text-3xl' },
        lg: { width: 160, strokeWidth: 12, fontSize: 'text-4xl' },
    };
    
    const config = sizes[size] || sizes.md;
    const radius = (config.width - config.strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = (score / 100) * circumference;
    const dashOffset = circumference - progress;

    return (
        <div className="flex flex-col items-center" data-testid="risk-gauge">
            <div className="relative" style={{ width: config.width, height: config.width }}>
                {/* Background Circle */}
                <svg
                    className="transform -rotate-90"
                    width={config.width}
                    height={config.width}
                >
                    <circle
                        cx={config.width / 2}
                        cy={config.width / 2}
                        r={radius}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={config.strokeWidth}
                        className="text-muted/30"
                    />
                    <circle
                        cx={config.width / 2}
                        cy={config.width / 2}
                        r={radius}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={config.strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={dashOffset}
                        className="transition-all duration-1000 ease-out"
                        style={{
                            filter: `drop-shadow(0 0 8px ${stroke}40)`
                        }}
                    />
                </svg>
                
                {/* Score Display */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={cn("font-number font-bold", config.fontSize)}>
                        {score}
                    </span>
                    <span className="text-xs text-muted-foreground">
                        {language === 'en' ? 'Risk' : 'जोखिम'}
                    </span>
                </div>
            </div>
            
            {/* Label */}
            {showLabel && (
                <div className={cn(
                    "mt-3 px-4 py-1.5 rounded-full text-sm font-medium text-white",
                    bg
                )}>
                    {label}
                </div>
            )}
        </div>
    );
};

export default RiskGauge;
